import { useMemo, useState, type ComponentType } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, CheckCircle2, Clock, Eye, FileText, Loader2, XCircle } from 'lucide-react'
import { Badge, Button, DataTable, type DataTableColumn, type DataTableExpansion } from '@atrvd/saas-ui'
import { formatDateTime } from '@dcp/shared/utils/formatters'
import { SectionHeading } from '@/components/common/SectionHeading'
import { pageOrCountError, queryErrorDisplay, tableQueryState } from '@/components/common/queryErrorDisplay'
import { useFileImports, useFileImportsCount } from '@/hooks/useBanking'
import { useTablePagination } from '@/hooks/useTablePagination'
import { isImportStalled, STALLED_IMPORT_MS } from '@/lib/file-import-stall'
import { BANKING_KEYS } from '@/lib/query-keys'
import type { FileImportRow, FileImportStatus, ImportType } from '@/types/banking'
import { MAX_LISTED_ISSUES, issueCountLabel, listableIssues, type ImportIssue } from './importIssues'

const STALLED_IMPORT_MINUTES = Math.round(STALLED_IMPORT_MS / 60_000)

const EMPTY_IMPORTS: FileImportRow[] = []
const NO_ISSUES: ImportIssue[] = []

/** The listable issues of a row on the current page (computed once per page). */
type IssuesOf = (fileImport: FileImportRow) => ImportIssue[]

/** Details modal of an import type — opened by "See details" on a completed import. */
export type ImportDetailsModal = ComponentType<{ fileImport: FileImportRow | null; onClose: () => void }>

/** Per-row actions of an import type (e.g. a kebab menu). */
export type ImportRowActions = ComponentType<{ fileImport: FileImportRow }>

interface ImportHistoryTableProps {
  importType: ImportType
  /** Omitted = no "See details" button. */
  DetailsModal?: ImportDetailsModal
  /** Omitted = no actions column. */
  RowActions?: ImportRowActions
}

/**
 * Status icon of an import row.
 *
 * A stalled row must not keep the spinner: an animation is a claim that work is
 * ongoing, and on a row whose importer never reported back that claim is false.
 */
function StatusIcon({ status, stalled }: { status: FileImportStatus; stalled: boolean }) {
  if (stalled) return <AlertTriangle className="h-4 w-4 shrink-0 text-warning-500" aria-hidden="true" />
  switch (status) {
    case 'completed':
      return <CheckCircle2 className="h-4 w-4 shrink-0 text-success-500" aria-hidden="true" />
    case 'failed':
      return <XCircle className="h-4 w-4 shrink-0 text-destructive" aria-hidden="true" />
    case 'processing':
      // No info colour token in the saas-ui preset; blue matches the `info` Badge next to it.
      return <Loader2 className="h-4 w-4 shrink-0 animate-spin text-blue-500" aria-hidden="true" />
    case 'pending':
      return <Clock className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
  }
}

/**
 * Status badge of an import row.
 *
 * "Stalled" is derived on read rather than stored: the row's status stays
 * whatever the importer last wrote, because "nothing reported back" is not the
 * same claim as "the import failed". The wording says precisely that.
 */
function StatusBadge({ status, stalled }: { status: FileImportStatus; stalled: boolean }) {
  if (stalled) {
    return (
      <Badge
        variant="warning"
        title={`No update from the importer for over ${STALLED_IMPORT_MINUTES} minutes. The import may never have started; re-upload the file.`}
      >
        Stalled
      </Badge>
    )
  }
  switch (status) {
    case 'completed':
      return <Badge variant="success">Completed</Badge>
    case 'failed':
      return <Badge variant="destructive">Failed</Badge>
    case 'processing':
      return <Badge variant="info">Processing</Badge>
    case 'pending':
      return <Badge variant="secondary">Pending</Badge>
  }
}

/**
 * Issue list of one import, shown in the row's detail panel. Catalog-level
 * warnings are already dropped by `listableIssues`, so the Issues column's
 * count and these lines cannot disagree. The list scrolls on its own, so it is
 * focusable to be scrollable from the keyboard.
 */
function ImportIssueList({ fileName, issues }: { fileName: string; issues: ImportIssue[] }) {
  return (
    <ul
      tabIndex={0}
      aria-label={`Issues in ${fileName}`}
      className="max-h-40 space-y-0.5 overflow-y-auto rounded bg-muted/50 p-2 text-xs"
    >
      {issues.slice(0, MAX_LISTED_ISSUES).map((issue, index) => (
        // Entries carry no id and repeat freely (same row, same message); the list is static.
        <li key={index}>
          {issue.row > 0 ? `Row ${issue.row}: ` : ''}
          {issue.message}
        </li>
      ))}
      {issues.length > MAX_LISTED_ISSUES && (
        <li className="text-muted-foreground">... and {issues.length - MAX_LISTED_ISSUES} more issues</li>
      )}
    </ul>
  )
}

/**
 * A row's issues open in a detail panel under it — offered whenever any issue
 * was recorded, completed imports included: a run can finish `completed` with
 * `failed_rows = 0` and still carry notes about rows that did import. The
 * toggle's name stays the same open or closed (`aria-expanded` carries the
 * state) and names the file, since every row has one.
 */
function buildExpansion(issuesOf: IssuesOf): DataTableExpansion<FileImportRow> {
  return {
    isExpandable: (fileImport) => issuesOf(fileImport).length > 0,
    renderDetail: (fileImport) => <ImportIssueList fileName={fileImport.file_name} issues={issuesOf(fileImport)} />,
    toggleLabel: (fileImport) => `Issues in ${fileImport.file_name}`,
  }
}

/**
 * Columns of the history table. "See details" and the actions column exist
 * only when the import type provides the modal / the row actions.
 */
function buildColumns(
  issuesOf: IssuesOf,
  onViewDetails: ((fileImport: FileImportRow) => void) | null,
  RowActions: ImportRowActions | undefined,
): DataTableColumn<FileImportRow>[] {
  const columns: DataTableColumn<FileImportRow>[] = [
    {
      key: 'status',
      header: 'Status',
      render: (fileImport) => {
        // Computed once and shared by icon and badge so the two cannot disagree —
        // a spinner next to a "Stalled" badge would be worse than either.
        const stalled = isImportStalled(fileImport)
        return (
          <div className="flex items-center gap-2">
            <StatusIcon status={fileImport.status} stalled={stalled} />
            <StatusBadge status={fileImport.status} stalled={stalled} />
          </div>
        )
      },
      cellClassName: 'whitespace-nowrap',
    },
    {
      key: 'file_name',
      header: 'File',
      render: (fileImport) => (
        <div className="max-w-[320px] truncate font-medium" title={fileImport.file_name}>
          {fileImport.file_name}
        </div>
      ),
    },
    {
      key: 'created_at',
      header: 'Uploaded',
      render: (fileImport) => (
        <div>
          <div className="whitespace-nowrap">{formatDateTime(fileImport.created_at)}</div>
          {fileImport.profiles?.full_name && (
            <div className="text-xs text-muted-foreground">{fileImport.profiles.full_name}</div>
          )}
        </div>
      ),
    },
    {
      key: 'rows',
      header: 'Rows',
      render: (fileImport) =>
        fileImport.status === 'completed' ? `${fileImport.imported_rows}/${fileImport.total_rows} rows` : '—',
      cellClassName: 'whitespace-nowrap text-muted-foreground',
    },
    {
      key: 'failed_rows',
      header: 'Failed',
      render: (fileImport) =>
        fileImport.status === 'completed' && fileImport.failed_rows > 0 ? (
          // Warning, not destructive: the import completed and only some rows
          // were rejected — red would read as the whole import having failed.
          <span className="flex items-center gap-1 text-warning-600">
            <AlertTriangle className="h-3 w-3" aria-hidden="true" />
            {fileImport.failed_rows} failed
          </span>
        ) : (
          '—'
        ),
      cellClassName: 'whitespace-nowrap',
    },
    {
      // The count on screen; the row's chevron toggle opens the list itself.
      key: 'issues',
      header: 'Issues',
      render: (fileImport) => {
        const count = issuesOf(fileImport).length
        return count > 0 ? issueCountLabel(count) : '—'
      },
      cellClassName: 'whitespace-nowrap text-muted-foreground',
    },
  ]
  if (onViewDetails) {
    columns.push({
      key: 'details',
      header: 'Details',
      render: (fileImport) =>
        fileImport.status === 'completed' && fileImport.imported_rows > 0 ? (
          <Button variant="outline" size="sm" onClick={() => onViewDetails(fileImport)} className="gap-1.5">
            <Eye className="h-3.5 w-3.5" aria-hidden="true" />
            See details
          </Button>
        ) : (
          '—'
        ),
      cellClassName: 'text-muted-foreground',
    })
  }
  if (RowActions) {
    columns.push({
      key: 'actions',
      header: 'Actions',
      render: (fileImport) => <RowActions fileImport={fileImport} />,
      width: '80px',
    })
  }
  return columns
}

/**
 * Import History of one import type: a server-paged list of the organization's
 * file imports with status, row counts, per-row issues, "See details" and the
 * type's row actions. Polls while an import is live (inside `useFileImports`);
 * a poll does not dim the table — only placeholder rows of another page do.
 */
export function ImportHistoryTable({ importType, DetailsModal, RowActions }: ImportHistoryTableProps) {
  const queryClient = useQueryClient()
  const countQuery = useFileImportsCount({ importType })
  // Deleting the only row of the last page shrinks the total; the hook moves
  // back to the new last page.
  const pagination = useTablePagination({ totalItems: countQuery.data })
  const rowsQuery = useFileImports({ limit: pagination.pageSize, offset: pagination.offset, importType })
  const rows = rowsQuery.data ?? EMPTY_IMPORTS

  const tableState = tableQueryState(
    {
      isPending: rowsQuery.isPending,
      isPlaceholderData: rowsQuery.isPlaceholderData,
      // Only a refetch of a FAILED query is a retry. The page query polls every
      // 2s while an import is live; counting that would flip the banner's
      // button to "Retrying…" on every poll while the count is failing.
      isFetching:
        (rowsQuery.error !== null && rowsQuery.isFetching) || (countQuery.error !== null && countQuery.isFetching),
      error: pageOrCountError(rowsQuery.error, countQuery.error),
      refetch: () => {
        void rowsQuery.refetch()
        void countQuery.refetch()
      },
    },
    rows.length,
  )
  const errorDisplay = queryErrorDisplay(tableState, rows.length)

  // The clicked row itself, not its id: a completed import never changes, and
  // looking it up in the current page would close the modal as soon as a new
  // import (or a poll) pushes it off the page.
  const [detailsImport, setDetailsImport] = useState<FileImportRow | null>(null)
  // One `listableIssues` pass per page, shared by the Issues column, the
  // toggle and the detail panel.
  const issuesById = useMemo(
    () => new Map(rows.map((fileImport) => [fileImport.id, listableIssues(fileImport.errors)])),
    [rows],
  )
  const table = useMemo(() => {
    const issuesOf: IssuesOf = (fileImport) => issuesById.get(fileImport.id) ?? NO_ISSUES
    return {
      columns: buildColumns(issuesOf, DetailsModal ? setDetailsImport : null, RowActions),
      expansion: buildExpansion(issuesOf),
    }
  }, [issuesById, DetailsModal, RowActions])

  return (
    <section className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <SectionHeading size="section" description="Recent file imports and their results">
          Import History
        </SectionHeading>
        {/* Always rendered so the heading does not shift when the first page lands. */}
        <Button
          variant="outline"
          size="sm"
          disabled={rowsQuery.isPending}
          onClick={() => queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allFileImports })}
        >
          Refresh
        </Button>
      </div>

      {errorDisplay.banner}
      <DataTable
        columns={table.columns}
        data={rows}
        rowKey={(fileImport) => fileImport.id}
        itemName="imports"
        showColumnControls={false}
        loading={tableState.loading}
        refreshing={tableState.refreshing}
        error={errorDisplay.tableError}
        expansion={table.expansion}
        emptyState={
          <div className="flex flex-col items-center justify-center text-muted-foreground">
            <FileText className="mb-2 h-8 w-8 opacity-50" aria-hidden="true" />
            <p className="text-sm">No imports yet</p>
            <p className="mt-1 text-xs">Upload a file to get started</p>
          </div>
        }
        pagination={pagination.dataTablePagination}
      />

      {DetailsModal && <DetailsModal fileImport={detailsImport} onClose={() => setDetailsImport(null)} />}
    </section>
  )
}
